import { useState, useEffect, useRef } from 'react';

interface UseTourAutoPlayProps {
  isOpen: boolean;
  currentStep: number;
  totalSteps: number;
  onNextStep: () => void;
  onEndTour: () => void;
}

export const useTourAutoPlay = ({
  isOpen,
  currentStep,
  totalSteps,
  onNextStep,
  onEndTour,
}: UseTourAutoPlayProps): {
  isAutoPlaying: boolean;
  isPaused: boolean;
  timeRemaining: number;
  togglePause: () => void;
  toggleAutoPlay: () => void;
  pauseAutoPlay: () => void;
  resumeAutoPlay: () => void;
} => {
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(20); // 20 seconds per step
  const autoPlayInterval = useRef<ReturnType<typeof setInterval> | null>(null);

  // Store functions in refs to avoid dependency issues
  const onNextStepRef = useRef(onNextStep);
  const onEndTourRef = useRef(onEndTour);

  // Update refs when functions change
  useEffect(() => {
    onNextStepRef.current = onNextStep;
  }, [onNextStep]);

  useEffect(() => {
    onEndTourRef.current = onEndTour;
  }, [onEndTour]);

  // Reset timer when step changes
  useEffect(() => {
    if (isOpen && isAutoPlaying) {
      setTimeRemaining(20);
    }
  }, [currentStep, isOpen, isAutoPlaying]);

  // Auto-play timer effect
  useEffect(() => {
    if (isAutoPlaying && !isPaused && isOpen) {
      autoPlayInterval.current = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            // Move to next step
            if (currentStep < totalSteps - 1) {
              onNextStepRef.current();
              return 20; // Reset timer
            } else {
              // End tour
              setIsAutoPlaying(false);
              onEndTourRef.current();
              return 0;
            }
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        if (autoPlayInterval.current) {
          clearInterval(autoPlayInterval.current);
        }
      };
    }
  }, [isAutoPlaying, isPaused, isOpen, currentStep, totalSteps]);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (autoPlayInterval.current) {
        clearInterval(autoPlayInterval.current);
      }
    };
  }, []);

  const togglePause = (): void => {
    setIsPaused(!isPaused);
  };

  const toggleAutoPlay = (): void => {
    setIsAutoPlaying(!isAutoPlaying);
    if (!isAutoPlaying) {
      setTimeRemaining(20);
    }
  };

  const pauseAutoPlay = (): void => {
    setIsAutoPlaying(false);
  };

  const resumeAutoPlay = (): void => {
    setIsAutoPlaying(true);
    setIsPaused(false);
    setTimeRemaining(20);
  };

  return {
    isAutoPlaying,
    isPaused,
    timeRemaining,
    togglePause,
    toggleAutoPlay,
    pauseAutoPlay,
    resumeAutoPlay,
  };
};
